import React from 'react';

/**
 * @param text - The text to highlight
 * @param searchQuery - The search query containing keywords
 * @param highlightColor - The color to use for highlighting (default: #C2BDDE)
 * @returns React element with highlighted text
 */
export const highlightText = (
  text: string | React.ReactNode,
  searchQuery: string,
  highlightColor: string = '#C2BDDE'
): React.ReactNode => {
  if (typeof text !== 'string') {
    return text;
  }
  
  if (!searchQuery || searchQuery.trim() === '') {
    return text;
  }

  const keywords = searchQuery
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(word => word.length >= 2);

  if (keywords.length === 0) {
    return text;
  }

  const pattern = keywords
    .map(keyword => {
      const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return `\\b${escaped}\\b`;
    })
    .join('|');
  
  const regex = new RegExp(`(${pattern})`, 'gi');

  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, index) => {
        const isMatch = part && keywords.some(
          keyword => part.toLowerCase() === keyword.toLowerCase()
        );

        if (isMatch) {
          return (
            <mark
              key={index}
              style={{
                backgroundColor: highlightColor,
                padding: '0 2px',
                borderRadius: '2px',
                fontWeight: 500,
                color: '#000',
              }}
            >
              {part}
            </mark>
          );
        }

        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </>
  );
};

/**
 * Hook to get highlighted text renderer
 * @param searchQuery - The search query
 * @param highlightColor - The color to use for highlighting
 * @returns Function to highlight text
 */
export const useHighlightText = (
  searchQuery: string,
  highlightColor: string = '#C2BDDE'
) => {
  return (text: string | React.ReactNode) => highlightText(text, searchQuery, highlightColor);
};
